import type { Inbox } from "@agency/core";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { callAction } from "../api";
import { Conversation, SmsFactsBar } from "../components/Conversation";
import { Badge, Card, EmptyState, ErrorState, LoadingState, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { shortDate } from "../theme";

export function InboxScreen() {
  const t = useT();
  const { locale } = useLocale();
  const [params, setParams] = useSearchParams();
  const selected = params.get("c") ?? "";
  const q = useQuery({
    queryKey: ["inbox"],
    queryFn: async () => (await callAction<"agency.message.list", Inbox>("agency.message.list", { limit: 300, unreadOnly: false })).result,
    refetchInterval: 15_000,
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const inbox = q.data;
  const threads = [...inbox.threads].sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
  const current = threads.find((x) => x.customerId === selected);
  const unreadTotal = threads.reduce((n, th) => n + th.unread, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t("inbox.title")} {unreadTotal > 0 ? <span className="ml-1 inline-flex min-w-[28px] items-center justify-center rounded-full bg-primary px-2 text-sm font-bold text-white align-middle">{unreadTotal}</span> : null}</h1>
          <p className="text-ink/60">{t("inbox.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-ink/55">{t("inbox.channel.email")}</span><Badge tone={inbox.emailMode === "live" ? "green" : "neutral"}>{inbox.emailMode === "live" ? t("msg.state.live") : t("msg.state.demo")}</Badge>
          <Badge tone={inbox.sendWindowOpen ? "green" : "amber"}>{inbox.sendWindowOpen ? t("inbox.windowOpen") : t("inbox.quietHours")}</Badge>
        </div>
      </div>

      <SmsFactsBar sms={inbox.sms} />

      {threads.length === 0 ? <EmptyState title={t("inbox.none")} body={inbox.sms.blocker ?? t("msg.liveNote")} /> : null}

      {threads.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-[320px_1fr]">
          <Card className={cn("overflow-hidden", current && "hidden md:block")}>
            <ul className="divide-y divide-ink/5 max-h-[70vh] overflow-y-auto">
              {threads.map((th) => (
                <li key={th.customerId}>
                  <button
                    onClick={() => setParams({ c: th.customerId })}
                    className={cn("flex w-full flex-col gap-1 px-4 py-3 text-left min-h-tap hover:bg-primary/8", selected === th.customerId && "bg-primary/10")}
                    aria-current={selected === th.customerId}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-semibold truncate">{th.customerName}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        <Badge tone={th.preferredLocale === "es" ? "amber" : "neutral"}>{th.preferredLocale.toUpperCase()}</Badge>
                        {th.unread > 0 ? <span className="min-w-[22px] rounded-full bg-primary px-1.5 text-center text-xs font-bold text-white">{th.unread}</span> : null}
                      </span>
                    </span>
                    <span className="text-xs text-ink/60 truncate w-full">{th.lastPreview}</span>
                    <span className="flex flex-wrap items-center gap-1 text-[11px] text-ink/50">
                      {shortDate(th.lastMessageAt, locale)}
                      <ConsentChip on={th.consent.smsTransactional} label={t("inbox.consent.smsTransactional")} />
                      <ConsentChip on={th.consent.smsMarketing} label={t("inbox.consent.smsMarketing")} />
                      <ConsentChip on={th.consent.emailMarketing} label={t("inbox.consent.emailMarketing")} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-4 min-h-[60vh] flex flex-col">
            {current ? (
              <>
                <div className="flex items-center justify-between gap-2 pb-2 border-b border-ink/10 mb-2">
                  <Link to={`/customers/${current.customerId}`} className="font-bold hover:underline min-h-[36px] inline-flex items-center">{current.customerName} →</Link>
                  <button className="md:hidden min-h-[36px] text-sm font-semibold text-primary-dark" onClick={() => setParams({})}>← {t("inbox.title")}</button>
                </div>
                <div className="flex-1 min-h-0">
                  <Conversation customerId={current.customerId} meta={inbox} />
                </div>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 text-ink/55">
                <MessageSquare className="h-8 w-8" />
                <p>{t("inbox.selectThread")}</p>
              </div>
            )}
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function ConsentChip({ on, label }: { on: boolean; label: string }) {
  return <span className={cn("rounded-full px-1.5 py-0.5", on ? "bg-primary/15 text-primary-dark" : "bg-ink/5 text-ink/40 line-through")}>{label}</span>;
}
